import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'react-final-form';
import createStyles from '@guestyci/foundation/createStyles';
import Paper from '@guestyci/foundation/Paper';
import useTheme from '@guestyci/foundation/useTheme';
import { minLength } from '@guestyci/foundation/validators';
import useTranslations from '@guestyci/localize/useTranslations';
import {
  allowLowercase,
  allowSpecialChapter,
  allowUppercase,
  hasDigit,
} from 'app/utils/validators';
import { ReactComponent as SuccessIcon } from 'resources/success.svg';
import { ReactComponent as ErrorIcon } from 'resources/error.svg';
import Translations from '../Translations';

const useStyles = createStyles((theme) => ({
  root: {
    position: 'relative',
  },

  hint: {
    display: 'none',
    whiteSpace: 'nowrap',
    color: 'white',
    padding: '1rem',
    left: 'calc(100% + 25px)',
    position: 'absolute',
    transform: 'translateY(-50%)',
    zIndex: 10,
    top: '50%',

    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 'calc(50% - 10px)',
      right: '100%',
      width: '0',
      height: '0',
      border: '10px solid transparent',
      borderRightColor: theme.palette.secondary.default,
    },
  },
  '@media (max-width: 768px)': {
    hint: {
      left: 'auto',
      right: '-25px',
      bottom: 'calc(100% + 25px)',
      top: 'auto',
      transform: 'none',

      '&::before': {
        top: '100%',
        right: 'auto',
        left: '80%',
        borderRightColor: 'transparent',
        borderTopColor: theme.palette.secondary.default,
      },
    },
  },
}));

const PasswordHint = ({ fieldName = 'password' }) => {
  const classes = useStyles();
  const field = useField(fieldName);
  const theme = useTheme();
  const password = field?.input?.value;
  const isActive = field?.meta?.active;

  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasSymbol, setHasSymbol] = useState(false);
  const [hasEnoughLength, setEnoughLength] = useState(false);
  const [hasHasDigit, setHasDigit] = useState(false);
  const [i18nLength, i18nLetterCase, i18nCharacter, i18nDigit] = useTranslations(
    Translations.step1.form.validationErrors
  );

  useEffect(() => {
    setHasUppercase(!allowUppercase()(password));
    setHasLowercase(!allowLowercase()(password ?? ''));
    setHasDigit(!hasDigit()(password));
    setHasSymbol(!allowSpecialChapter()(password));
    setEnoughLength(!minLength(8)(password));
  }, [password]);

  const hasValidationError =
    !hasUppercase || !hasLowercase || !hasSymbol || !hasEnoughLength || !hasHasDigit;
  const showPasswordHint = !!password?.length && hasValidationError;

  const getIcon = (success) => (
    <SuccessIcon
      width={24}
      height={24}
      className={success ? 'fill-green' : 'fill-gray-dark'}
    />
  );

  return (
    <div className={classes.root} id="password-hint-container">
      {password &&
        (hasValidationError ? (
          isActive || <ErrorIcon width={24} height={24} className="fill-red" />
        ) : (
          <SuccessIcon width={24} height={24} className="fill-green" />
        ))}
      {showPasswordHint && (
        <Paper
          bgColor={theme.palette.secondary.default}
          className={`${classes.hint} hint-paper`}
          noPadding
        >
          <div id="password-hint-length">
            <span className="pr-1">{getIcon(hasEnoughLength)}</span>
            <span>{i18nLength}</span>
          </div>
          <div id="password-hint-lowercase-uppercase">
            <span className="pr-1">{getIcon(hasUppercase && hasLowercase)}</span>
            <span>{i18nLetterCase}</span>
          </div>
          <div id="password-hint-digit">
            <span className="pr-1">{getIcon(hasHasDigit)}</span>
            <span>{i18nDigit}</span>
          </div>
          <div id="password-hint-symbol">
            <span className="pr-1">{getIcon(hasSymbol)}</span>
            <span>{i18nCharacter}</span>
          </div>
        </Paper>
      )}
    </div>
  );
};

PasswordHint.propTypes = {
  fieldName: PropTypes.string,
};

PasswordHint.displayName = 'PasswordHint';

export default React.memo(PasswordHint);
